import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { 
  CloudProviderType, 
  CustomerType, 
  EnvironmentType,
  AzureDetails,
  GCPDetails,
  AWSDetails
} from '../../types/environment';
import { FormContainer, FormField, FormSelect, FormTextarea, FormCheckbox, FormCard, FormButtons, FormAlert } from '../../components/forms';

interface Company {
  id: string;
  name: string;
}

const EnvironmentCreate: React.FC = () => {
  const navigate = useNavigate();
  const [isProviderDetailsEnabled, setIsProviderDetailsEnabled] = useState(false);
  const [savedEnvironmentId, setSavedEnvironmentId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    cloudProviderType: '' as CloudProviderType | '',
    customerType: 'ExistingCustomer' as CustomerType,
    companyId: '',
    environmentType: 'Dev' as EnvironmentType,
  });

  // Cloud provider details state
  const [azureDetails, setAzureDetails] = useState<AzureDetails>({});
  const [gcpDetails, setGCPDetails] = useState<GCPDetails>({});
  const [awsDetails, setAWSDetails] = useState<AWSDetails>({});

  // Fetch companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
  });

  // Create environment mutation
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post('/environments', data);
      return response.data;
    },
    onSuccess: (data) => {
      setSavedEnvironmentId(data.id);
      setIsProviderDetailsEnabled(true);
      toast.success('Environment created successfully. You can now add cloud provider details.');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create environment');
    },
  });

  // Update environment mutation (for adding cloud provider details)
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await api.patch(`/environments/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Cloud provider details saved successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save cloud provider details');
    },
  });

  // Provision environment mutation
  const provisionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.post(`/environments/${id}/provision`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Provisioning request ${data.requestNumber} created successfully`);
      navigate(`/environment-requests/${data.requestId}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create provisioning request');
    },
  });

  const handleInitialSave = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.cloudProviderType || !formData.companyId) {
      toast.error('Please fill in all required fields');
      return;
    }

    await createMutation.mutateAsync(formData);
  };

  const handleSaveProviderDetails = async () => {
    if (!savedEnvironmentId) return;

    const updateData: any = {};
    
    if (formData.cloudProviderType === 'Azure' && Object.keys(azureDetails).length > 0) {
      updateData.azureDetails = azureDetails;
    } else if (formData.cloudProviderType === 'GCP' && Object.keys(gcpDetails).length > 0) {
      updateData.gcpDetails = gcpDetails;
    } else if (formData.cloudProviderType === 'AWS' && Object.keys(awsDetails).length > 0) {
      updateData.awsDetails = awsDetails;
    }

    if (Object.keys(updateData).length > 0) {
      await updateMutation.mutateAsync({ id: savedEnvironmentId, data: updateData });
    }
  };

  const handleProvision = async () => {
    if (!savedEnvironmentId) return;
    
    // Save provider details first if they exist
    await handleSaveProviderDetails();
    
    // Then create provisioning request
    await provisionMutation.mutateAsync(savedEnvironmentId);
  };

  const renderProviderFields = () => {
    if (!isProviderDetailsEnabled) {
      return (
        <FormAlert
          type="info"
          title="Configuration Locked"
          message="Save the environment first to enable cloud provider configuration"
        />
      );
    }

    switch (formData.cloudProviderType) {
      case 'Azure':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Azure Configuration</h3>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                label="Application ID"
                name="applicationId"
                value={azureDetails.applicationId || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, applicationId: e.target.value })}
                placeholder="Enter Azure application ID"
              />

              <FormField
                label="Subscription ID"
                name="subscriptionId"
                value={azureDetails.subscriptionId || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, subscriptionId: e.target.value })}
                placeholder="Enter Azure subscription ID"
              />

              <FormField
                label="Tenant ID"
                name="tenantId"
                value={azureDetails.tenantId || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, tenantId: e.target.value })}
                placeholder="Enter Azure tenant ID"
              />

              <FormField
                label="Client Secret"
                name="clientSecret"
                type="password"
                value={azureDetails.clientSecret || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, clientSecret: e.target.value })}
                placeholder="Enter Azure client secret"
              />

              <FormField
                label="Subscription Name"
                name="subscriptionName"
                value={azureDetails.subscriptionName || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, subscriptionName: e.target.value })}
                placeholder="Enter subscription name"
              />

              <FormField
                label="Tenant Name"
                name="tenantName"
                value={azureDetails.tenantName || ''}
                onChange={(e) => setAzureDetails({ ...azureDetails, tenantName: e.target.value })}
                placeholder="Enter tenant name"
              />

              <FormCheckbox
                label="CSP Subscription"
                name="cspSubscription"
                checked={azureDetails.cspSubscription || false}
                onChange={(e) => setAzureDetails({ ...azureDetails, cspSubscription: e.target.checked })}
                description="Check if this is a CSP subscription"
                className="sm:col-span-2"
              />
            </div>
          </div>
        );

      case 'GCP':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Google Cloud Platform Configuration</h3>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                label="Project Name"
                name="projectName"
                value={gcpDetails.projectName || ''}
                onChange={(e) => setGCPDetails({ ...gcpDetails, projectName: e.target.value })}
                placeholder="Enter GCP project name"
              />

              <FormField
                label="Service Account"
                name="serviceAccount"
                value={gcpDetails.serviceAccount || ''}
                onChange={(e) => setGCPDetails({ ...gcpDetails, serviceAccount: e.target.value })}
                placeholder="Enter service account email"
              />

              <FormField
                label="Client ID"
                name="clientId"
                value={gcpDetails.clientId || ''}
                onChange={(e) => setGCPDetails({ ...gcpDetails, clientId: e.target.value })}
                placeholder="Enter GCP client ID"
              />

              <FormField
                label="Client Secret"
                name="clientSecret"
                type="password"
                value={gcpDetails.clientSecret || ''}
                onChange={(e) => setGCPDetails({ ...gcpDetails, clientSecret: e.target.value })}
                placeholder="Enter GCP client secret"
              />

              <FormTextarea
                label="JWT Provider"
                name="jwtProvider"
                value={gcpDetails.jwtProvider || ''}
                onChange={(e) => setGCPDetails({ ...gcpDetails, jwtProvider: e.target.value })}
                rows={3}
                placeholder="Enter JWT provider configuration"
                className="sm:col-span-2"
              />
            </div>
          </div>
        );

      case 'AWS':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">AWS Configuration</h3>
            
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <FormField
                label="Access Key ID"
                name="clientId"
                value={awsDetails.clientId || ''}
                onChange={(e) => setAWSDetails({ ...awsDetails, clientId: e.target.value })}
                placeholder="Enter AWS access key ID"
              />

              <FormField
                label="Secret Access Key"
                name="clientSecretKey"
                type="password"
                value={awsDetails.clientSecretKey || ''}
                onChange={(e) => setAWSDetails({ ...awsDetails, clientSecretKey: e.target.value })}
                placeholder="Enter AWS secret access key"
              />
            </div>
          </div>
        );

      case 'Rackspace':
        return (
          <FormAlert
            type="info"
            title="Rackspace Configuration"
            message="Rackspace configuration will be handled through their portal. Click 'Provision' to create the request."
          />
        );

      case 'OnPremise':
        return (
          <FormAlert
            type="info"
            title="On-Premise Environment"
            message="On-Premise environments don't require cloud credentials. Click 'Provision' to create the request."
          />
        );

      default:
        return null;
    }
  };

  return (
    <FormContainer
      title="Create New Environment"
      subtitle="Set up a new cloud environment with provider configuration"
      backTo="/environments"
      backText="Back to Environments"
      maxWidth="6xl"
    >
      <form onSubmit={handleInitialSave} className="space-y-6">
        <FormCard
          title="Basic Information"
          subtitle="Provide the basic details for the new environment"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <FormField
              label="Environment Name"
              name="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={savedEnvironmentId !== null}
              placeholder="Enter environment name"
            />

            <FormSelect
              label="Cloud Provider Type"
              name="cloudProviderType"
              value={formData.cloudProviderType}
              onChange={(e) => setFormData({ ...formData, cloudProviderType: e.target.value as CloudProviderType })}
              required
              disabled={savedEnvironmentId !== null}
              placeholder="Select a provider"
              options={[
                { value: 'Azure', label: 'Azure' },
                { value: 'AWS', label: 'AWS' },
                { value: 'GCP', label: 'Google Cloud Platform' },
                { value: 'Rackspace', label: 'Rackspace' },
                { value: 'OnPremise', label: 'On-Premise' }
              ]}
            />

            <FormSelect
              label="Customer Type"
              name="customerType"
              value={formData.customerType}
              onChange={(e) => setFormData({ ...formData, customerType: e.target.value as CustomerType })}
              required
              disabled={savedEnvironmentId !== null}
              placeholder="Select customer type"
              options={[
                { value: 'ExistingCustomer', label: 'Existing Customer' },
                { value: 'NewOnboarding', label: 'New Onboarding' }
              ]}
            />

            <FormSelect
              label="Company"
              name="companyId"
              value={formData.companyId}
              onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
              required
              disabled={savedEnvironmentId !== null}
              placeholder="Select a company"
              options={(companiesData?.data || []).map((company: Company) => ({
                value: company.id,
                label: company.name
              }))}
            />

            <FormSelect
              label="Environment Type"
              name="environmentType"
              value={formData.environmentType}
              onChange={(e) => setFormData({ ...formData, environmentType: e.target.value as EnvironmentType })}
              required
              disabled={savedEnvironmentId !== null}
              placeholder="Select environment type"
              options={[
                { value: 'Dev', label: 'Development' },
                { value: 'Stage', label: 'Staging' },
                { value: 'Prod', label: 'Production' },
                { value: 'UAT', label: 'UAT' }
              ]}
            />
          </div>
        </FormCard>

        {formData.cloudProviderType && (
          <FormCard
            title="Cloud Provider Configuration"
            subtitle="Configure the credentials and settings for the selected cloud provider"
          >
            {renderProviderFields()}
          </FormCard>
        )}

        {!savedEnvironmentId ? (
          <FormButtons
            onCancel={() => navigate('/environments')}
            submitText="Save Environment"
            isSubmitting={createMutation.isPending}
          />
        ) : (
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/environments')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
            >
              Back to Environments
            </button>
            {isProviderDetailsEnabled && (
              <>
                <button
                  type="button"
                  onClick={handleSaveProviderDetails}
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {updateMutation.isPending ? (
                    <div className="flex items-center">
                      <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      </div>
                      Saving...
                    </div>
                  ) : (
                    'Save Provider Details'
                  )}
                </button>
                
                <button
                  type="button"
                  onClick={handleProvision}
                  disabled={provisionMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {provisionMutation.isPending ? (
                    <div className="flex items-center">
                      <div className="animate-spin -ml-1 mr-3 h-4 w-4 text-white">
                        <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                      </div>
                      Creating Request...
                    </div>
                  ) : (
                    'Provision Environment'
                  )}
                </button>
              </>
            )}
          </div>
        )}
      </form>
    </FormContainer>
  );
};

export default EnvironmentCreate;